import { useSWRConfig } from "swr";
import useSWRMutation from "swr/mutation";
import type { ICompany } from "types/company.types";
import type { IProject } from "types/project.types";
import type { ITask } from "types/task.types";
import { api } from "utils/axios";
import { invalidateProject } from "./invalidateProject";

export function useTask() {
	const { mutate } = useSWRConfig();
	// A task write is shown on the Projects pages and, for a close, on the
	// close's own page: refresh both, whoever made the write.
	const refresh = (data: Partial<ITask>) => {
		const project = (data as { project?: unknown }).project;
		const id =
			typeof project === "number"
				? project
				: Number((project as { id?: unknown } | undefined)?.id);
		void invalidateProject(mutate, Number.isFinite(id) && id ? id : undefined);
	};
	const { trigger: createTask, isMutating: createLoading } = useSWRMutation(
		`tasks`,
		createFetcher,
	);
	const { trigger: updateTask, isMutating: updateLoading } = useSWRMutation(
		`tasks`,
		updateFetcher,
	);
	const { trigger: removeTask, isMutating: removeLoading } = useSWRMutation(
		`tasks`,
		deleteFetcher,
	);
	const {
		data: task,
		trigger: getTask,
		isMutating: getLoading,
	} = useSWRMutation(`tasks`, getFetcher);

	const create = async (data: Partial<ITask>, onSuccess?: () => void) => {
		const result = await createTask(data, { onSuccess });
		refresh(data);
		return result;
	};

	const get = async (data: Partial<ITask>, onSuccess?: () => void) => {
		const result = await getTask(data, { onSuccess });
		return result;
	};

	const update = async (data: Partial<ITask>, onSuccess?: () => void) => {
		const result = await updateTask(data, { onSuccess });
		refresh(data);
		return result;
	};

	const remove = async (data: Partial<ITask>, onSuccess?: () => void) => {
		const result = await removeTask(data, { onSuccess });
		refresh(data);
		return result;
	};

	return {
		create,
		createLoading,
		update,
		updateLoading,
		remove,
		removeLoading,
		get,
		getLoading,
		project: task,
	};
}

interface IUpdateData {
	arg: Partial<ICompany> | Partial<IProject>;
}

const createFetcher = (url: string, { arg }: IUpdateData) => {
	return api.post(url, arg).then((res) => res.data);
};

/**
 * PATCH tasks/:id refuses some writes with a 200 `{success:false,message}`
 * (a signed-off close, a close step's assignee). Resolving that as a success
 * showed "Task dates updated" on a refusal and closed the task modal on a
 * dropped save (/qa r10 B-4, B-5). It rejects instead, shaped like an axios
 * error so every `error?.response?.data?.message` catch shows the reason.
 */
export const updateFetcher = (url: string, { arg }: IUpdateData) => {
	return api.patch(url + "/" + arg.id, arg).then((res) => {
		if (res.data?.success === false) {
			const error = new Error(
				res.data.message || "The task was not updated.",
			) as Error & { response: { data: unknown } };
			error.response = { data: res.data };
			throw error;
		}
		return res.data;
	});
};

const getFetcher = (url: string, { arg }: IUpdateData) => {
	return api.get(url + "/" + arg.id).then((res) => res.data);
};

const deleteFetcher = (url: string, { arg }: IUpdateData) => {
	return api.delete(url + "/" + arg.id).then((res) => res.data);
};
